import { useTheme } from 'styled-components';
import { createElement as rc, useContext, useEffect, useCallback, useState, useMemo, Fragment } from 'react';
import logging from '@sstdev/lib_logging';
import Logo from '../Logo';
import {
    View,
    GestureContext,
    Text,
    fromTheme,
    styled,
    ScrollView,
    webOnlyStyles,
    nativeOnlyStyles
} from 'lib_ui-primitives';
import { NavbarContext } from '../appLayout/NavBarContextProvider.gl2025';
import propTypes from 'prop-types';
import sizes from 'lib_ui-primitives/src/styling/themes/gl2025/sizes';
import { metadata } from 'lib_ui-services';

let Sidebar = styled(View).attrs({ name: 'sidebar' })`
    position: absolute;
    top: 0;
    left: 0;
    z-index: ${({ theme }) => theme.zindex.Drawer};
    height: 100%;
    flex-basis: auto;
    flex-direction: column;
    justify-content: space-between;
    background-color: ${fromTheme('sideBar', 'backgroundColor')};
    padding: ${({ theme, navLocked }) => {
        return theme.mobile && !navLocked ? '0' : `0 0 0 ${theme.sideBar?.padding}px`;
    }};
    border-right: ${fromTheme('sideBar', 'borderWidth')} solid ${fromTheme('titleBar', 'borderColor')};
    width: ${({ theme, navOpen, navLocked }) =>
        navOpen || navLocked ? theme.sideBar?.width : theme.sideBar?.widthIconOnly || 0}px;
    transition: width 0.3s ease-in-out;
    ${({ theme }) => {
        return theme.smallMobile ? `padding-top: ${theme.sideBar.padding}px;` : '';
    }}

    /* this needs to be added to the parent of the scrollable area to style scrollbars */
    * {
        &::-webkit-scrollbar {
            width: ${sizes.fixedSpacing.md}px;
            height: ${sizes.fixedSpacing.md}px;
        }
        &::-webkit-scrollbar-track {
            background: ${fromTheme('scrollbarTrack')};
            border-radius: ${sizes.radius.full}px;
        }
        &::-webkit-scrollbar-thumb {
            background: ${fromTheme('scrollbarThumb')};
            border-radius: ${sizes.radius.full}px;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: ${fromTheme('scrollbarThumbHover')};
        }
        &::-webkit-scrollbar-corner {
            background: transparent;
        }
    }
`;
Sidebar.displayName = 'Sidebar';

Sidebar = webOnlyStyles(Sidebar)`
    box-shadow: ${({ navOpen, navLocked, theme }) => (navOpen && !navLocked ? theme.sideBar?.floatingShadow : 'none')};
`;

Sidebar = nativeOnlyStyles(Sidebar)`
    elevation: ${({ theme }) => theme.sideBar?.elevation || 8};
`;

const SideNavContent = styled(ScrollView).attrs({ name: 'sidenav-content', role: 'navigation' })`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${fromTheme('outerMenus', 'gap')};
    padding-right: ${fromTheme('sideBar', 'padding')};
    overflow-y: auto;
    transition: all 0.3s ease-in-out;
`;
SideNavContent.displayName = 'SideNavContent';

const SideNavFooter = styled(View).attrs({ name: 'sidenav-footer' })`
    width: 100%;
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    flex-shrink: 0;
    align-items: center;
    padding-top: ${({ theme }) => 2 * theme.sideBar?.padding}px;
    padding-bottom: ${({ hasTagLine, theme }) => (hasTagLine ? theme.sideBar?.padding : 2 * theme.sideBar?.padding)}px;
    padding-left: 0;
    padding-right: ${fromTheme('sideBar', 'padding')};
    gap: ${fromTheme('outerMenus', 'gap')};
    ${({ theme }) => {
        return `padding: ${theme.sideBar?.padding}px ${theme.sideBar?.padding}px ${theme.sideBar?.padding}px 0`;
    }}
`;
SideNavFooter.displayName = 'SideNavFooter';

const Tagline = styled(Text).attrs({ name: 'tagline' })`
    align-self: center;
    ${fromTheme('sideBar', 'taglineFont')};
    color: ${fromTheme('sideBar', 'tagLineColor')};
    white-space: nowrap;
    overflow: hidden;
`;

const MobileOverlay = styled(View).attrs({ name: 'mobile-overlay' })`
    position: fixed;
    top: ${({ theme }) => (theme.smallMobile ? '0' : theme.titleBar?.height)}px;
    width: ${({ theme }) => (theme.smallMobile ? '10%' : '25%')};
    right: 0;
    opacity: ${({ navOpen }) => (navOpen ? '1' : '0')};
    height: ${({ theme }) => (theme.smallMobile ? '100%' : `calc(100% - ${theme.titleBar?.height}px)`)};
    background-color: rgba(0, 0, 0, 0.6);
    transition: ${({ navOpen }) => (navOpen ? 'opacity 0.1s ease-in-out 0.2s' : '')};
    display: ${({ navOpen }) => navOpen ? 'flex' : 'none' };
`;
MobileOverlay.displayName = 'MobileOverlay';

export const _private = {
    getUseCaseTagline: () => metadata.getUseCaseTagline()
};

/**
 * Navigation container/wrapper providing space for menu (in a collapsible drawer)
 * Does NOT provide navigation by itself. That is implemented in navHeading:NavHeading
 */
function SideNav(props) {
    const { onSwipeLeft, onSwipeRight } = useContext(GestureContext);
    const { ready, open: navOpen, setOpen, locked: navLocked, setLocked } = useContext(NavbarContext);
    const theme = useTheme();

    const [position, setPosition] = useState('relative');

    const tagLine = useMemo(
        // for testing, you can add something like
        // () => (theme.smallMobile ? '' : _private.getUseCaseTagline() || 'Tracking Made Easy™'),
        () => (theme.smallMobile ? '' : _private.getUseCaseTagline()),
        [theme.smallMobile]
    );

    useEffect(() => {
        // prettier-ignore
        let unsubscribes = [
            onSwipeLeft(() => {
                logging.debug('[MENU] Drag Close');
                return setOpen(false);
            }),
            onSwipeRight(() => {
                logging.debug('[MENU] Drag Open');
                return setOpen(true);
            })
        ];
        return () => unsubscribes.forEach(u => u());
    }, [onSwipeLeft, onSwipeRight, setOpen]);

    const { children = [] } = props;

    const onMouseOver = useCallback(() => {
        if (!theme.mobile && !navLocked && !navOpen) {
            setOpen(true);
        }
    }, [setOpen, navLocked, navOpen, theme.mobile]);

    const onMouseLeave = useCallback(() => {
        if (!theme.mobile && !navLocked && navOpen) {
            setOpen(false);
        }
    }, [setOpen, navLocked, navOpen, theme.mobile]);

    const closeSideNav = useCallback(() => {
        setOpen(false);
        setLocked(false);
    }, [setLocked, setOpen]);

    useEffect(() => {
        if (position === 'relative' && navOpen && !navLocked) {
            setPosition('absolute');
        } else if (position === 'absolute' && !navOpen) {
            setTimeout(() => setPosition('relative'), 300);
        }
    }, [navOpen, navLocked, position]);

    if (!ready) {
        return null;
    }

    // prettier-ignore
    return rc(Fragment, null,
            rc(Sidebar, { onMouseOver, onMouseLeave, navOpen, navLocked },
                rc(SideNavContent, null, children),
                !theme.native && rc(SideNavFooter, { key: 'side-nav-footer', hasTagLine: tagLine && navOpen },

                    rc(Logo, {
                        variant: (!navOpen && !navLocked) ? 'small' : 'color',
                        // styling the logo element using styled components doesn't work. use inline styles instead
                        style: { width: 'auto', maxWidth: theme.sideBar?.logoMaxWidth, maxHeight: theme.sideBar?.logoMaxHeight}
                    }),
                    tagLine && navOpen && rc(Tagline, null, tagLine)
                ),
                // this does not work on RN.... Less important right now (1/6/2026)
            theme.mobile && rc(MobileOverlay, { onClick: closeSideNav, navOpen })
        )
    );
}

SideNav.propTypes = {
    children: propTypes.node
};
export default SideNav;
